// Booking panel: the advance payment (part of the rental price paid early)
// and, when Finance & Books is on, what this booking looks like in the ledger.
//
// The advance payment is deliberately separate from the refundable security
// deposit above it: one is money the customer has paid towards the rental,
// the other is money the business is holding and owes back.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpenCheck, CircleDollarSign, RotateCcw } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { PAYMENT_METHODS, docTypeLabel, fmtMinor, idemHeaders } from "../../lib/books";
import { Button, Field, Money, Pill, Table, inputClass } from "./shared";

const STATUS_LABELS = {
  not_required: "Not required",
  pending: "Waiting for the advance payment",
  partially_received: "Partly received",
  received: "Received",
  partially_applied: "Partly applied to the rental charge",
  applied: "Applied to the rental charge",
  partially_refunded: "Partly refunded",
  refunded: "Refunded",
};

export default function BookingFinancePanel({ bookingId, onChanged }) {
  const [advance, setAdvance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);      // "collect" | "refund"
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ amount: "", method: "cash", reference: "", note: "", reason: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/advance-payment`);
      setAdvance(data);
    } catch (err) {
      setAdvance(null);
    }
    try {
      const { data } = await api.get(`/finance/bookings/${bookingId}/summary`);
      setSummary(data);
    } catch (err) {
      setSummary(null);      // Books off, or no finance access: panel stays advance-only
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  if (!advance) return null;
  const required = advance.advance_payment_required || 0;
  const held = advance.advance_payment_held_amount || 0;
  const nothingToShow = required <= 0 && (advance.advance_payment_received_amount || 0) <= 0 && !summary;
  if (nothingToShow) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const path = form === "collect" ? "collect" : "refund";
      const body = form === "collect"
        ? { amount: state.amount, method: state.method, reference: state.reference || null,
            note: state.note || null }
        : { amount: state.amount, method: state.method, reference: state.reference || null,
            reason: state.reason };
      await api.post(`/bookings/${bookingId}/advance-payment/${path}`, body, { headers: idemHeaders() });
      toast.success(form === "collect" ? "Advance payment recorded." : "Advance payment refunded.");
      setForm(null);
      setState({ amount: "", method: "cash", reference: "", note: "", reason: "" });
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="booking-finance-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <CircleDollarSign className="w-4 h-4 text-slate-500" /> Advance payment
          </h2>
          <p className="text-sm text-slate-600">
            Part of the rental price paid before the rental. Not a refundable security deposit.
          </p>
        </div>
        <Pill status={held > 0 ? "ready_to_post" : "posted"}>
          {STATUS_LABELS[advance.advance_payment_status] || advance.advance_payment_status}
        </Pill>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        {[["Required", required], ["Received", advance.advance_payment_received_amount],
          ["Applied", advance.advance_payment_applied_amount], ["Held now", held]].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 p-2">
            <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
            <div className="font-medium tabular-nums">
              ₱{Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => { setForm("collect"); setState({ ...state, amount: "" }); }}
                data-testid="advance-collect-btn">Record advance payment</Button>
        {held > 0 && (
          <Button variant="secondary" onClick={() => { setForm("refund"); setState({ ...state, amount: "" }); }}
                  data-testid="advance-refund-btn">
            <RotateCcw className="w-4 h-4" /> Refund advance
          </Button>
        )}
      </div>

      {form && (
        <div className="mt-3 rounded-xl border border-slate-200 p-3 grid sm:grid-cols-2 gap-3"
             data-testid="advance-form">
          <Field label="Amount (₱)">
            <input className={inputClass} inputMode="decimal" value={state.amount} data-testid="advance-amount"
                   onChange={(e) => setState({ ...state, amount: e.target.value })} />
          </Field>
          <Field label="Method">
            <select className={inputClass} value={state.method} data-testid="advance-method"
                    onChange={(e) => setState({ ...state, method: e.target.value })}>
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          {form === "refund" ? (
            <Field label="Reason for the refund" className="sm:col-span-2">
              <input className={inputClass} value={state.reason} data-testid="advance-reason"
                     onChange={(e) => setState({ ...state, reason: e.target.value })} />
            </Field>
          ) : (
            <Field label="Reference or note (optional)" className="sm:col-span-2">
              <input className={inputClass} value={state.note}
                     onChange={(e) => setState({ ...state, note: e.target.value })} />
            </Field>
          )}
          <div className="sm:col-span-2 flex gap-2">
            <Button onClick={submit} busy={busy} data-testid="advance-save">Save</Button>
            <Button variant="secondary" onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-4 border-t border-slate-200 pt-3" data-testid="booking-books-summary">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium text-slate-900 inline-flex items-center gap-2">
              <BookOpenCheck className="w-4 h-4 text-slate-500" /> In the books
            </h3>
            <Link to="/app/finance" className="text-sm text-slate-600 underline">Open Finance &amp; Books</Link>
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div className="rounded-xl border border-slate-200 p-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Still owed</div>
              <div className="font-medium"><Money minor={summary.receivable_open_minor} /></div>
            </div>
            <div className="rounded-xl border border-slate-200 p-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Deposit held</div>
              <div className="font-medium"><Money minor={summary.deposit_held_minor} /></div>
            </div>
            <div className="rounded-xl border border-slate-200 p-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Advance held</div>
              <div className="font-medium"><Money minor={summary.advance_held_minor} /></div>
            </div>
          </div>
          {summary.draft && (
            <p className="mt-2 text-sm text-slate-600" data-testid="booking-draft-state">
              Rental charge: <span className="font-medium">{summary.draft.status.replace(/_/g, " ")}</span>
              {" · "}{fmtMinor(summary.draft.net_minor)}
              {(summary.draft.flags || []).some((f) => f.blocking && !f.resolved) &&
                <span className="text-amber-700"> · needs review before posting</span>}
            </p>
          )}
          {(summary.documents || []).length > 0 && (
            <div className="mt-2">
              <Table head={["Entry", "Date", { key: "a", label: "Amount", align: "right" }]}>
                {summary.documents.slice(0, 8).map((doc) => (
                  <tr key={doc.id}>
                    <td className="py-2 pr-3">
                      <div className="text-slate-900">{doc.number}</div>
                      <div className="text-xs text-slate-500">{docTypeLabel(doc.doc_type)}
                        {doc.status !== "posted" && ` · ${doc.status}`}</div>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{doc.date}</td>
                    <td className="py-2 pr-3 text-right"><Money minor={doc.total_minor} /></td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
